// The ONE owner of history.state for the journey (v7 browser-history
// integration). Every chain change becomes a real history entry (push), so
// Back/Forward - buttons, browser chrome, the Android back gesture - walk the
// journey like a browser walks pages, and going back never erases where you
// just were.
//
// Mechanics: each entry we own carries { mm: { id, route } } with a MONOTONIC
// id from a module-global sequence. Ids stay stack-ascending even after the
// browser drops forward entries (stack [0,1,2], back to 1, push -> [0,1,3]),
// so traversal direction is a plain comparison and "can go forward" is
// curId < topId (the browser exposes no forward-stack state itself).
// Entries created by fragment navigations (parasha picks, saved-trail resume)
// arrive without our state; ensureStamped adopts them as the new top - which
// is correct, because a fragment navigation also dropped the forward stack.

interface MMHistoryState {
  mm: { id: number; route: string };
}

// Ids must stay monotonic ACROSS page loads too: history entries stamped by a
// PREVIOUS load persist in the session stack, so a reload that restarted the
// sequence at 0 would mint ids that compare wrongly against the surviving
// ones (Back onto an old entry would read as "forward"). init() therefore
// seeds the sequence from the wall clock - every new load's ids are strictly
// above every previous load's.
let seq = 0; // monotonic id source (epoch-seeded in init; 0 only under tests)
let curId = 0; // id of the entry we are standing on
let topId = 0; // newest id still in the stack (forward boundary)
let baseId = 0; // first in-app entry; back below this exits the app

function mmState(id: number, route: string): MMHistoryState {
  return { mm: { id, route } };
}

function readId(state: unknown): number | null {
  if (typeof state !== "object" || state === null) return null;
  const mm = (state as { mm?: unknown }).mm;
  if (typeof mm !== "object" || mm === null) return null;
  const id = (mm as { id?: unknown }).id;
  return typeof id === "number" ? id : null;
}

// Seed the CURRENT entry (cold load) without creating a new one.
export function init(route: string): void {
  // strictly above any previous load's ids (see header note); the max() also
  // keeps repeated inits monotonic within one clock millisecond
  seq = Math.max(Date.now(), seq + 1);
  curId = seq;
  topId = seq;
  baseId = seq;
  history.replaceState(mmState(seq, route), "", route || location.pathname + location.search);
}

export function currentRoute(): string {
  return location.hash;
}

// Push a new journey state. No-ops (returns false) when the route already
// matches - re-renders and idempotent applies must never spam the stack.
export function pushRoute(route: string): boolean {
  if (!route || route === currentRoute()) return false;
  const id = ++seq;
  history.pushState(mmState(id, route), "", route);
  curId = id;
  topId = id;
  return true;
}

// Rewrite the current entry's URL keeping its id. An empty route must NOT be
// passed as the URL argument - replaceState resolves "" against the document
// base, which would strip the fragment entirely (same fallback contract as
// init/ensureStamped).
export function replaceRoute(route: string): void {
  history.replaceState(mmState(curId, route), "", route || location.pathname + location.search);
}

// A traversal landed (popstate): adopt the entry's id and report direction.
export function notePop(state: unknown): "back" | "forward" {
  const id = readId(state);
  if (id === null) return "forward"; // unstamped entry; ensureStamped adopts it
  const dir = id < curId ? "back" : "forward";
  curId = id;
  return dir;
}

// Adopt an entry that arrived WITHOUT our state (fragment navigation or a
// pre-app entry we traversed back onto): stamp it as the new top.
export function ensureStamped(route: string): void {
  if (readId(history.state) !== null) return;
  const id = ++seq;
  history.replaceState(mmState(id, route), "", route || undefined);
  curId = id;
  topId = id;
}

export function canGoForward(): boolean {
  return curId < topId;
}

export function canGoBackInApp(): boolean {
  return curId > baseId;
}

// Test-only.
export function _resetForTests(): void {
  seq = 0;
  curId = 0;
  topId = 0;
  baseId = 0;
}
